<template>
  <Button class="outcome-button" :class="modifiers" :disabled="disabled" :title="title">
    <i v-if="icon" class="outcome-button__icon" :class="`pi pi-${icon}`" />
    <span class="outcome-button__label">
      <slot>{{ label }}</slot>
    </span>
    <span v-if="sub" class="outcome-button__sub">{{ sub }}</span>
    <span v-if="hotkey" class="outcome-button__hotkey">{{ hotkey }}</span>
  </Button>
</template>

<script setup>
import { computed } from 'vue';
import Button from 'primevue/button';

// The big chunky action button in the console's outcome cluster — Sell, Floor,
// No Sale, Offer, Undo.
//
// TONES ARE NAMED FOR THE VERB, NOT THE HUE. They were `yellow`, `teal` and
// `accent` on the reference, which is the exact habit the component token
// contract exists to break: `tone="no-sale"` says what the button does, and the
// theme decides that No Sale is yellow — in `extend.outcome`, next to the
// other two outcome hues no semantic token could express. The purple `accent`
// tone went with the rename; nothing ever passed it and the brand has no purple
// for it to mean.
//
// The ten-color palette used to be a script-side object of hexes and shadows
// spread onto an inline style. Every one of those is a modifier class and a
// token now.
//
// SLOTS
//   default — the label, when it needs markup (a hard line break). Falls back
//             to the `label` prop.
defineOptions({ name: 'OutcomeButton' });

const props = defineProps({
  // sell | floor | no-sale | offer | info | danger | neutral | ghost | danger-ghost
  tone: {
    type: String,
    default: 'neutral',
    validator: (value) =>
      ['sell', 'floor', 'no-sale', 'offer', 'info', 'danger', 'neutral', 'ghost', 'danger-ghost'].includes(value),
  },
  label: { type: String, default: '' },
  // Second line under the label — a price, a count.
  sub: { type: String, default: null },
  // Keyboard shortcut, printed small in the corner. The console does not bind
  // it; the application owns key handling.
  hotkey: { type: String, default: null },
  // PrimeIcons name without the `pi-` prefix.
  icon: { type: String, default: null },
  disabled: { type: Boolean, default: false },
  // The oversized treatment, for the primary outcomes.
  big: { type: Boolean, default: false },
  // comfortable | compact
  density: { type: String, default: 'comfortable', validator: (value) => ['comfortable', 'compact'].includes(value) },
  title: { type: String, default: null },
});

// `--big-compact` carries the tighter padding only; it is applied ALONGSIDE
// `--big`, which keeps the oversized type. Two independent modifiers would need
// a compound selector, and a compound selector has no unambiguous token path.
const modifiers = computed(() => {
  const compact = props.density === 'compact';
  return [
    `outcome-button--${props.tone}`,
    props.big ? 'outcome-button--big' : null,
    props.big && compact ? 'outcome-button--big-compact' : null,
    !props.big && compact ? 'outcome-button--compact' : null,
    props.disabled ? 'outcome-button--disabled' : null,
  ].filter(Boolean);
});
</script>

<style scoped>
/* The `.p-button` prefix out-ranks Aura's own base background, without which
   none of the tone fills below paint. */
.p-button.outcome-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 64px;
  position: relative;
  text-align: center;
  font-family: inherit;
  transition: all 140ms cubic-bezier(0.2, 0, 0, 1);
  border-radius: var(--p-outcome-button-border-radius);
  border-width: var(--p-outcome-button-border-width);
  border-style: solid;
  padding: var(--p-outcome-button-padding);
  gap: var(--p-outcome-button-gap);
  line-height: var(--p-outcome-button-line-height);
  letter-spacing: var(--p-outcome-button-letter-spacing);
}

.p-button.outcome-button--compact {
  min-height: 56px;
  padding: var(--p-outcome-button-compact-padding);
}

.p-button.outcome-button--big {
  min-height: 92px;
  padding: var(--p-outcome-button-big-padding);
}

.p-button.outcome-button--big-compact {
  min-height: 72px;
  padding: var(--p-outcome-button-big-compact-padding);
}

.p-button.outcome-button--disabled {
  opacity: 0.42;
  cursor: not-allowed;
  box-shadow: none;
}

/* ── Tones ──────────────────────────────────────────────────────────────────
   Each tone's lift is its own fill at 28%, so a theme that moves the fill moves
   the glow with it. The reference dropped the glow entirely in light mode via a
   `[data-theme]` selector; that is gone, because a component cannot key off a
   scheme attribute the application happens to set. A tinted shadow under a
   solid fill reads correctly on paper too. */

.p-button.outcome-button--sell {
  background: var(--p-outcome-button-sell-background);
  color: var(--p-outcome-button-sell-color);
  border-color: var(--p-outcome-button-sell-border-color);
  box-shadow: var(--p-outcome-button-sell-box-shadow);
}

.p-button.outcome-button--sell:hover {
  background: var(--p-outcome-button-sell-hover-background);
}

.p-button.outcome-button--floor {
  background: var(--p-outcome-button-floor-background);
  color: var(--p-outcome-button-floor-color);
  border-color: var(--p-outcome-button-floor-border-color);
  box-shadow: var(--p-outcome-button-floor-box-shadow);
}

.p-button.outcome-button--floor:hover {
  background: var(--p-outcome-button-floor-hover-background);
}

.p-button.outcome-button--no-sale {
  background: var(--p-outcome-button-no-sale-background);
  color: var(--p-outcome-button-no-sale-color);
  border-color: var(--p-outcome-button-no-sale-border-color);
  box-shadow: var(--p-outcome-button-no-sale-box-shadow);
}

.p-button.outcome-button--no-sale:hover {
  background: var(--p-outcome-button-no-sale-hover-background);
}

.p-button.outcome-button--offer {
  background: var(--p-outcome-button-offer-background);
  color: var(--p-outcome-button-offer-color);
  border-color: var(--p-outcome-button-offer-border-color);
  box-shadow: var(--p-outcome-button-offer-box-shadow);
}

.p-button.outcome-button--offer:hover {
  background: var(--p-outcome-button-offer-hover-background);
}

.p-button.outcome-button--info {
  background: var(--p-outcome-button-info-background);
  color: var(--p-outcome-button-info-color);
  border-color: var(--p-outcome-button-info-border-color);
  box-shadow: var(--p-outcome-button-info-box-shadow);
}

.p-button.outcome-button--info:hover {
  background: var(--p-outcome-button-info-hover-background);
}

.p-button.outcome-button--danger {
  background: var(--p-outcome-button-danger-background);
  color: var(--p-outcome-button-danger-color);
  border-color: var(--p-outcome-button-danger-border-color);
  box-shadow: var(--p-outcome-button-danger-box-shadow);
}

.p-button.outcome-button--danger:hover {
  background: var(--p-outcome-button-danger-hover-background);
}

.p-button.outcome-button--neutral {
  background: var(--p-outcome-button-neutral-background);
  color: var(--p-outcome-button-neutral-color);
  border-color: var(--p-outcome-button-neutral-border-color);
  box-shadow: var(--p-outcome-button-neutral-box-shadow);
}

.p-button.outcome-button--neutral:hover {
  background: var(--p-outcome-button-neutral-hover-background);
}

.p-button.outcome-button--ghost {
  background: var(--p-outcome-button-ghost-background);
  color: var(--p-outcome-button-ghost-color);
  border-color: var(--p-outcome-button-ghost-border-color);
}

.p-button.outcome-button--ghost:hover {
  background: var(--p-outcome-button-ghost-hover-background);
}

.p-button.outcome-button--danger-ghost {
  background: var(--p-outcome-button-danger-ghost-background);
  color: var(--p-outcome-button-danger-ghost-color);
  border-color: var(--p-outcome-button-danger-ghost-border-color);
}

.p-button.outcome-button--danger-ghost:hover {
  background: var(--p-outcome-button-danger-ghost-hover-background);
}

/* ── Parts ──────────────────────────────────────────────────────────────── */

.outcome-button__icon {
  font-size: var(--p-outcome-button-icon-font-size);
  margin-bottom: 2px;
}

.outcome-button--big .outcome-button__icon {
  font-size: var(--p-outcome-button-big-icon-font-size);
}

.outcome-button__label {
  font-size: var(--p-outcome-button-label-font-size);
  font-weight: var(--p-outcome-button-label-font-weight);
  letter-spacing: var(--p-outcome-button-label-letter-spacing);
  text-transform: uppercase;
}

.outcome-button--big .outcome-button__label {
  font-size: var(--p-outcome-button-big-label-font-size);
}

.outcome-button__sub {
  font-size: var(--p-outcome-button-sub-font-size);
  font-weight: var(--p-outcome-button-sub-font-weight);
  opacity: 0.75;
  text-transform: none;
  letter-spacing: 0;
}

.outcome-button--big .outcome-button__sub {
  font-size: var(--p-outcome-button-big-sub-font-size);
}

.outcome-button__hotkey {
  position: absolute;
  top: 6px;
  right: 8px;
  font-size: var(--p-outcome-button-hotkey-font-size);
  font-weight: var(--p-outcome-button-hotkey-font-weight);
  font-family: var(--p-outcome-button-hotkey-font-family);
  letter-spacing: 0;
  opacity: 0.55;
}
</style>
